import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { participantsApi } from '../../api/participants';
import { ParticipantEditor, ParticipantSummary } from '../../components/ParticipantEditor';
import { ParticipantHistoryPanel } from '../../components/ParticipantHistory';
import { ProblemAlert, StatusBadge } from '../../components/ui';
import { UsersPanel } from '../../components/UsersPage';
import { useAuth } from '../../auth/AuthContext';
import { isCerAdminRole } from '../../auth/roles';

export function ParticipantDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const participant = useQuery({ queryKey: ['participants', 'detail', id], queryFn: () => participantsApi.get(id) });

  if (participant.isLoading) return <p className="muted">Loading…</p>;
  if (participant.error || !participant.data) return <ProblemAlert error={participant.error ?? new Error('Not found')} />;
  const p = participant.data;

  return (
    <>
      <p className="crumbs">
        <Link to="/cer/participants">Participants</Link> / {p.legalName}
      </p>
      <div className="row">
        <h1>{p.legalName}</h1>
        <StatusBadge status={p.status} />
      </div>
      {p.status === 'pending' && <p className="notice">This participant has an application in progress; its status is decided through the application.</p>}

      <ParticipantSummary participant={p} />

      <ParticipantEditor
        key={p.version}
        participant={p}
        mode="cer"
        onSave={(patch) => participantsApi.update(p.id, patch)}
        onSaved={(updated) => {
          qc.setQueryData(['participants', 'detail', id], updated);
          void qc.invalidateQueries({ queryKey: ['participants'] });
        }}
      />

      <ParticipantHistoryPanel queryKey={['participants', 'history', id, p.version]} load={() => participantsApi.history(id)} />

      {p.status !== 'pending' && (
        <details className="panel">
          <summary>
            <strong>Users</strong> <span className="muted">— who can act for this participant</span>
          </summary>
          <UsersPanel participantId={p.id} canManage={isCerAdminRole(user?.roles)} />
        </details>
      )}
    </>
  );
}
